import { Clock, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DELIVERY_DATE_TYPE_OPTIONS, DELIVERY_WINDOW_TYPE_OPTIONS, PICKUP_DATE_TYPE_OPTIONS, PICKUP_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from 'src/app/MyQuote/AddQuote/Steps/addQuoteSteps.const';
import { DELIVERY_WINDOW_TOOLTIP, PICKUP_WINDOW_TOOLTIP } from 'src/app/MyQuote/CreateQuote/Steps/Steps.const';
import { isLegInTransitOrLater } from 'src/app/Orders/OrderView/LegOverview/legOverview.helpers';
import CommonModal from 'src/components/CommonModal';
import DateSelect from 'src/components/DateSelect/DateSelect';
import InputMaskCmp from 'src/components/InputMaskCmp';
import InputTextArea from 'src/components/InputTextArea';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import { getDimensionMaxByEquipment, MEASUREMENT } from 'src/constants/common';
import { useAppSelector } from 'src/redux/hooks';
import { selectCurrentLegData, selectOrder, selectOrderLegActiveTab } from 'src/redux/orders.slice';
import { logError } from 'src/services/LoggingService';
import { createEditRequest } from 'src/services/OrderService';
import { formatTimeInput, getDateWithSuffixFormat, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

import AddressSelectionSection, { IAddressData } from './AddressSelectionSection';
import LineItemsSection, { ILineItem } from './LineItemsSection';

interface IProps {
    handleClose: () => void;
    orderId: string;
    orderType?: string;
    existingLineItems?: any[];
    onSuccess?: (editRequest: any, activity?: any) => void;
}

interface IScheduleForm {
    pickupSensitivity: string;
    pickupDate: Date | null;
    pickupEndDate: Date | null;
    pickupWindowType: string;
    pickupStartTime: string;
    pickupEndTime: string;
    deliverySensitivity: string;
    deliveryDate: Date | null;
    deliveryEndDate: Date | null;
    deliveryWindowType: string;
    deliveryStartTime: string;
    deliveryEndTime: string;
}

const SCHEDULE_DEFAULTS: IScheduleForm = {
    pickupSensitivity: 'flexible',
    pickupDate: null,
    pickupEndDate: null,
    pickupWindowType: 'ready_time',
    pickupStartTime: '',
    pickupEndTime: '',
    deliverySensitivity: 'flexible',
    deliveryDate: null,
    deliveryEndDate: null,
    deliveryWindowType: 'delivery_time',
    deliveryStartTime: '',
    deliveryEndTime: '',
};

// Pickup-side edits stop making sense once the freight is on the truck (leg in transit or
// later): the pickup already happened and the shipper can't change. Delivery-side edits stay
// open until delivery. Line items are handled separately — they are order-level, not per-leg.
const PICKUP_SIDE_EDIT_TYPES = ['pickup_schedule', 'shipper_details'];

const toDayString = (d: Date | null) => (d ? moment(d).format('YYYY-MM-DD') : null);

// Single builders for the schedule payloads — used both for the request body AND for the
// "did anything actually change" baseline, so the comparison can never drift from what is sent.
const buildPickupEditData = (sv: IScheduleForm) => ({
    pickupSensitivity: sv.pickupSensitivity,
    pickupDate: toDayString(sv.pickupDate),
    pickupEndDate: sv.pickupSensitivity === 'flexible' ? toDayString(sv.pickupEndDate) : null,
    pickupWindowType: sv.pickupWindowType ?? null,
    pickupStartTime: sv.pickupWindowType && sv.pickupWindowType !== 'open' ? sv.pickupStartTime || null : null,
    pickupEndTime: sv.pickupWindowType === 'ready_time' ? sv.pickupEndTime || null : null,
});

const buildDeliveryEditData = (sv: IScheduleForm) => ({
    deliverySensitivity: sv.deliverySensitivity,
    deliveryDate: sv.deliverySensitivity !== 'open' ? toDayString(sv.deliveryDate) : null,
    deliveryEndDate: sv.deliverySensitivity === 'flexible' ? toDayString(sv.deliveryEndDate) : null,
    deliveryWindowType: sv.deliveryWindowType ?? null,
    deliveryStartTime: sv.deliveryWindowType && sv.deliveryWindowType !== 'open' ? sv.deliveryStartTime || null : null,
    deliveryEndTime: sv.deliveryWindowType === 'delivery_time' ? sv.deliveryEndTime || null : null,
});

const NEW_LINE_ITEM: ILineItem = {
    id: null,
    handlingUnit: 1,
    handlingUnitNo: 0,
    weight: 0,
    itemLength: 0,
    width: 0,
    height: 0,
    totalWeight: 0,
    freightClass: 0,
    commodityId: null,
    commodityName: null,
    isStack: true,
    weightMeasurement: 'lbs',
};

// order_dimensions row → editable line item. Shared by the prefill and the change baseline.
const mapExistingLineItems = (dims: any[]): ILineItem[] =>
    dims.map((dim) => ({
        id: dim.id ?? null,
        handlingUnit: dim.handlingUnit ?? null,
        handlingUnitNo: dim.handlingUnitNo ?? 0,
        weight: dim.weight ?? 0,
        itemLength: dim.length ?? 0,
        width: dim.width ?? 0,
        height: dim.height ?? 0,
        totalWeight: dim.totalWeight ?? 0,
        freightClass: dim.freightClass ?? 0,
        commodityId: dim.commodityId ?? null,
        commodityName: dim.commodity ?? null,
        isStack: dim.isStack ?? true,
        weightMeasurement: dim.weightMeasurement === 'KGS' ? 'kgs' : 'lbs',
    }));

// Only the fields the backend receives take part in the change check (commodityName is display-only).
const normalizeLineItem = (item: ILineItem) => ({
    handlingUnit: item.handlingUnit ?? null,
    handlingUnitNo: Number(item.handlingUnitNo) || 0,
    weight: Number(item.weight) || 0,
    itemLength: Number(item.itemLength) || 0,
    width: Number(item.width) || 0,
    height: Number(item.height) || 0,
    totalWeight: Number(item.totalWeight) || 0,
    freightClass: Number(item.freightClass) || 0,
    commodityId: item.commodityId ?? null,
    isStack: !!item.isStack,
    weightMeasurement: (item.weightMeasurement || 'lbs').toLowerCase(),
});

const isSameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const EditOrderModal = ({ handleClose, orderId, orderType, existingLineItems, onSuccess }: IProps) => {
    const { t } = useTranslation();

    const EDIT_TYPE_OPTIONS = useMemo(
        () => [
            { value: 'pickup_schedule', label: t('Orders.Pickup_Schedule') },
            { value: 'delivery_schedule', label: t('Orders.Delivery_Schedule') },
            { value: 'shipper_details', label: t('BookOrder.Shipper_Details') },
            { value: 'consignee_details', label: t('BookOrder.Consignee_Details') },
            { value: 'line_items', label: t('Orders.Line_Items') },
        ],
        [t]
    );

    const pickupScheduleSchema = useMemo(
        () =>
            Yup.object().shape({
                pickupSensitivity: Yup.string().required(t('Orders.Pickup_Sensitivity_Required')),
                pickupDate: Yup.date().nullable().required(t('Orders.Pickup_Start_Date_Required')),
                pickupEndDate: Yup.date()
                    .nullable()
                    .when('pickupSensitivity', {
                        is: 'flexible',
                        then: (schema) => schema.required(t('Orders.Pickup_End_Date_Required_For_Flexible')),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                pickupWindowType: Yup.string().required(t('Orders.Pickup_Window_Type_Required')),
                pickupStartTime: Yup.string()
                    .nullable()
                    .test('pickup-start-time-required', t('Orders.Pickup_Start_Time_Required'), function (value) {
                        const { pickupWindowType } = this.parent;

                        if (pickupWindowType && pickupWindowType !== 'open') {
                            return !!value;
                        }

                        return true;
                    }),
                pickupEndTime: Yup.string()
                    .nullable()
                    .test('pickup-end-time-required', t('Orders.Pickup_End_Time_Required'), function (value) {
                        const { pickupWindowType } = this.parent;

                        if (pickupWindowType === 'ready_time') {
                            return !!value;
                        }

                        return true;
                    })
                    .test('is-after-start', t('Orders.End_Time_Must_Be_After_Start_Time'), function (endTime) {
                        const { pickupStartTime, pickupWindowType } = this.parent;

                        if (pickupWindowType !== 'ready_time') return true;

                        if (!pickupStartTime || !endTime) return true;

                        const [startHour, startMin] = pickupStartTime.split(':').map(Number);
                        const [endHour, endMin] = endTime.split(':').map(Number);
                        const startMinutes = startHour * 60 + startMin;
                        const endMinutes = endHour * 60 + endMin;

                        return endMinutes > startMinutes;
                    }),
            }),
        [t]
    );

    const deliveryScheduleSchema = useMemo(
        () =>
            Yup.object().shape({
                deliverySensitivity: Yup.string().required(t('Orders.Delivery_Sensitivity_Required')),
                deliveryDate: Yup.date()
                    .nullable()
                    .when('deliverySensitivity', {
                        is: (val: string) => val === 'fixed' || val === 'flexible',
                        then: (schema) => schema.required(t('Orders.Delivery_Start_Date_Required')),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                deliveryEndDate: Yup.date()
                    .nullable()
                    .when('deliverySensitivity', {
                        is: 'flexible',
                        then: (schema) => schema.required(t('Orders.Delivery_End_Date_Required_For_Flexible')),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                deliveryWindowType: Yup.string().required(t('Orders.Delivery_Window_Type_Required')),
                deliveryStartTime: Yup.string()
                    .nullable()
                    .test('delivery-start-time-required', t('Orders.Delivery_Start_Time_Required'), function (value) {
                        const { deliveryWindowType } = this.parent;

                        if (deliveryWindowType && deliveryWindowType !== 'open') {
                            return !!value;
                        }

                        return true;
                    }),
                deliveryEndTime: Yup.string()
                    .nullable()
                    .test('delivery-end-time-required', t('Orders.Delivery_End_Time_Required'), function (value) {
                        const { deliveryWindowType } = this.parent;

                        if (deliveryWindowType === 'delivery_time') {
                            return !!value;
                        }

                        return true;
                    })
                    .test('is-after-start', t('Orders.End_Time_Must_Be_After_Start_Time'), function (endTime) {
                        const { deliveryStartTime, deliveryWindowType } = this.parent;

                        if (deliveryWindowType !== 'delivery_time') return true;

                        if (!deliveryStartTime || !endTime) return true;

                        const [startHour, startMin] = deliveryStartTime.split(':').map(Number);
                        const [endHour, endMin] = endTime.split(':').map(Number);
                        const startMinutes = startHour * 60 + startMin;
                        const endMinutes = endHour * 60 + endMin;

                        return endMinutes > startMinutes;
                    }),
            }),
        [t]
    );

    const { isShipperLoading } = useAppSelector((state) => state.addresses);
    const currentLegData = useAppSelector(selectCurrentLegData);
    const order = useAppSelector(selectOrder);
    const orderLegActiveTab = useAppSelector(selectOrderLegActiveTab);

    // Full leg objects (not the tab items) — option value = numeric order_legs.id,
    // the only safe leg key (leg 1's legId display string equals the order's orderId)
    const orderLegs: any[] = useMemo(() => (Array.isArray(order?.orderLegs) ? order.orderLegs : []), [order?.orderLegs]);
    const hasMultipleLegs = orderLegs.length > 1;

    const legOptions = useMemo(() => orderLegs.map((leg: any) => ({ value: `${leg?.id}`, label: `#${leg?.legId}` })), [orderLegs]);

    // Legs whose freight has already left the shipper — pickup-side edits are pointless for them.
    // Keyed by the same string id the leg dropdown uses.
    const pickupLockedLegIds = useMemo(() => new Set(orderLegs.filter((leg: any) => isLegInTransitOrLater(leg?.orderStatus)).map((leg: any) => `${leg?.id}`)), [orderLegs]);

    // No leg left to move a pickup on → hide pickup-side edit types entirely. Orders without a
    // legs array fall back to the order's own status so single-leg payloads behave the same.
    const isEveryLegPickupLocked = orderLegs.length > 0 ? orderLegs.every((leg: any) => pickupLockedLegIds.has(`${leg?.id}`)) : isLegInTransitOrLater(order?.orderStatus);

    // Line items describe the physical freight for the whole order. Once ANY leg is in transit
    // the goods are on a truck and their dimensions can't be re-requested.
    const isLineItemsLocked = pickupLockedLegIds.size > 0 || isLegInTransitOrLater(order?.orderStatus);

    // Leg the edit request targets. Default = the leg tab the customer is viewing;
    // Master tab defaults to Leg 1. Single-leg orders never show the dropdown.
    const [selectedLeg, setSelectedLeg] = useState<{ value: string; label: string } | null>(() => {
        if (orderLegs.length <= 1) return null;

        if (!orderLegActiveTab?.isMaster) {
            const activeLegOption = legOptions.find((opt) => opt.value === orderLegActiveTab?.tabValue);
            if (activeLegOption) return activeLegOption;
        }

        return legOptions[0] ?? null;
    });

    // Once the customer picks a leg by hand, edit-type changes must not override it
    const legTouchedRef = useRef(false);

    // All prefills below follow the DROPDOWN selection (not the page tab) so the
    // form always shows the values of the leg the customer is about to edit.
    const selectedLegData = useMemo(() => {
        if (!hasMultipleLegs || !selectedLeg) return currentLegData;

        return orderLegs.find((leg: any) => `${leg?.id}` === selectedLeg.value) ?? currentLegData;
    }, [hasMultipleLegs, selectedLeg, orderLegs, currentLegData]);

    // "Business Hours" on its own tells the customer nothing about THIS stop — the hours it refers
    // to are the ones saved on the shipper/consignee. Shown inline the same way the quote stepper
    // does it (AddQuote ConsigneeDetails), but read off the leg being edited rather than the address
    // book, so it reflects what the order actually carries. Falls back to the order-level party for
    // payloads whose legs don't carry one; null when no hours are saved, and the line then reads
    // exactly as it did before.
    const businessHoursRange = (start?: string | null, end?: string | null): string | null => {
        const from = start ? String(start).slice(0, 5) : null;
        const to = end ? String(end).slice(0, 5) : null;

        return from && to ? `${from} – ${to}` : null;
    };

    const shipperBusinessHours = useMemo(() => {
        const shipper = selectedLegData?.shipper ?? order?.shipper;

        return businessHoursRange(shipper?.businessHoursStartTime, shipper?.businessHoursEndTime);
    }, [selectedLegData?.shipper, order?.shipper]);

    const consigneeBusinessHours = useMemo(() => {
        const consignee = selectedLegData?.consignee ?? order?.consignee;

        return businessHoursRange(consignee?.businessHoursStartTime, consignee?.businessHoursEndTime);
    }, [selectedLegData?.consignee, order?.consignee]);

    const equipmentType: string = selectedLegData?.equipmentType ?? 'V';
    const equipmentTypeFullName: string = selectedLegData?.equipmentTypeFullName ?? 'Dry Van';

    const deliveryMaxDate = useMemo(() => {
        const schedule = selectedLegData?.pickupDeliverySchedule;
        const dateStr = schedule?.currentDeliveryEndDate || schedule?.currentDeliveryStartDate;
        if (!dateStr) return undefined;
        const dateOnly = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;

        return moment(dateOnly, 'YYYY-MM-DD').toDate();
    }, [selectedLegData?.pickupDeliverySchedule]);

    const pickupMinDate = useMemo(() => {
        if (deliveryMaxDate) {
            const maxDateOnly = moment(deliveryMaxDate).format('YYYY-MM-DD');
            const todayOnly = moment().format('YYYY-MM-DD');

            if (moment(maxDateOnly, 'YYYY-MM-DD').isBefore(moment(todayOnly, 'YYYY-MM-DD'), 'day')) {
                return undefined;
            }
        }

        return new Date();
    }, [deliveryMaxDate]);

    const pickupMinDateForDelivery = useMemo(() => {
        const schedule = selectedLegData?.pickupDeliverySchedule;
        const dateStr = schedule?.currentPickupEndDate || schedule?.currentPickupStartDate;
        if (!dateStr) return new Date();
        const dateOnly = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;

        return moment(dateOnly, 'YYYY-MM-DD').toDate();
    }, [selectedLegData?.pickupDeliverySchedule]);

    const schedulePrefill = useMemo<IScheduleForm>(() => {
        const schedule = selectedLegData?.pickupDeliverySchedule;
        if (!schedule) return SCHEDULE_DEFAULTS;

        const toDate = (val: any): Date | null => {
            if (!val) return null;
            const dateOnly = typeof val === 'string' ? val.split('T')[0] : val;

            return moment(dateOnly, 'YYYY-MM-DD').toDate();
        };
        const toTime = (val: any): string => (val ? String(val).slice(0, 5) : '');

        return {
            pickupSensitivity: schedule.currentPickupSensitivity || SCHEDULE_DEFAULTS.pickupSensitivity,
            pickupDate: toDate(schedule.currentPickupStartDate),
            pickupEndDate: toDate(schedule.currentPickupEndDate),
            pickupWindowType: schedule.currentPickupWindowType || SCHEDULE_DEFAULTS.pickupWindowType,
            pickupStartTime: toTime(schedule.currentPickupStartTime),
            pickupEndTime: toTime(schedule.currentPickupEndTime),
            deliverySensitivity: schedule.currentDeliverySensitivity || SCHEDULE_DEFAULTS.deliverySensitivity,
            deliveryDate: toDate(schedule.currentDeliveryStartDate),
            deliveryEndDate: toDate(schedule.currentDeliveryEndDate),
            deliveryWindowType: schedule.currentDeliveryWindowType || SCHEDULE_DEFAULTS.deliveryWindowType,
            deliveryStartTime: toTime(schedule.currentDeliveryStartTime),
            deliveryEndTime: toTime(schedule.currentDeliveryEndTime),
        };
    }, [selectedLegData?.pickupDeliverySchedule]);

    const addressPrefills = useMemo(() => {
        const build = (entity: any, addr: any): { id: number | null; data: IAddressData } | null => {
            if (!entity?.id && !addr?.fullAddress) return null;

            return {
                id: entity?.id ?? null,
                data: {
                    id: entity?.id ?? null,
                    companyName: entity?.companyName || '',
                    fullAddress: addr?.fullAddress || '',
                    address1: addr?.address1 || '',
                    city: addr?.city || '',
                    state: addr?.state || '',
                    stateCode: addr?.stateCode || '',
                    country: addr?.country || '',
                    countryCode: addr?.countryCode || '',
                    postal: addr?.postal || '',
                    latitude: addr?.latitude || '',
                    longitude: addr?.longitude || '',
                    streetNumber: addr?.streetNumber || '',
                    timezone: addr?.timezone || '',
                },
            };
        };

        return {
            shipper: build(selectedLegData?.shipper, selectedLegData?.shipper?.shipperAddress),
            consignee: build(selectedLegData?.consignee, selectedLegData?.consignee?.consigneeAddress),
        };
    }, [selectedLegData?.shipper, selectedLegData?.consignee]);

    // Adjacent-leg date boundaries. Legs run in sequence, so this leg's pickup can't
    // land before the PREVIOUS leg's delivery, and its delivery can't pass the NEXT
    // leg's pickup — the customer must fix the earlier leg's schedule first. Same-day
    // handoff is allowed (cross-docks deliver and pick up on the same day).
    const adjacentLegBounds = useMemo(() => {
        if (!hasMultipleLegs || !selectedLegData) return { prev: null, next: null };

        const idx = orderLegs.findIndex((leg: any) => leg?.id === selectedLegData?.id);
        if (idx === -1) return { prev: null, next: null };

        const toDay = (val: any): string | null => (val ? String(val).split('T')[0] : null);
        const prevLeg = idx > 0 ? orderLegs[idx - 1] : null;
        const nextLeg = idx < orderLegs.length - 1 ? orderLegs[idx + 1] : null;

        return {
            prev: prevLeg
                ? {
                      legId: prevLeg.legId,
                      // The previous leg finishes delivering by its END date (flexible windows)
                      deliveryDate: toDay(prevLeg.pickupDeliverySchedule?.currentDeliveryEndDate || prevLeg.pickupDeliverySchedule?.currentDeliveryStartDate),
                  }
                : null,
            next: nextLeg
                ? {
                      legId: nextLeg.legId,
                      pickupDate: toDay(nextLeg.pickupDeliverySchedule?.currentPickupStartDate),
                  }
                : null,
        };
    }, [hasMultipleLegs, selectedLegData, orderLegs]);

    // Edit type
    const [selectedEditType, setSelectedEditType] = useState<{ value: string; label: string } | null>(null);

    // Address state (for shipper/consignee) — plain state, unchanged
    const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
    const [addressData, setAddressData] = useState<IAddressData | null>(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    // Line items state — plain state, unchanged
    const [lineItems, setLineItems] = useState<ILineItem[]>([]);

    // Common state — plain state, unchanged
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    // Cross-leg date conflict from the last submit — shown as a red info icon with a
    // tooltip on the offending date field
    const [crossLegError, setCrossLegError] = useState<{ field: keyof IScheduleForm; message: string } | null>(null);

    const {
        control,
        watch,
        setValue,
        getValues,
        reset: resetSchedule,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<IScheduleForm>({ defaultValues: SCHEDULE_DEFAULTS });

    const pickupSensitivity = watch('pickupSensitivity');
    const pickupDate = watch('pickupDate');
    const pickupEndDate = watch('pickupEndDate');
    const pickupWindowType = watch('pickupWindowType');
    const deliverySensitivity = watch('deliverySensitivity');
    const deliveryDate = watch('deliveryDate');
    const deliveryEndDate = watch('deliveryEndDate');
    const deliveryWindowType = watch('deliveryWindowType');

    const isPickupScheduleEditType = selectedEditType?.value === 'pickup_schedule';
    const isDeliveryScheduleEditType = selectedEditType?.value === 'delivery_schedule';
    const isAddressEditType = selectedEditType?.value === 'shipper_details' || selectedEditType?.value === 'consignee_details';
    const isLineItemsEditType = selectedEditType?.value === 'line_items';
    const isShipper = selectedEditType?.value === 'shipper_details';

    const isPickupSideEditType = PICKUP_SIDE_EDIT_TYPES.includes(selectedEditType?.value ?? '');

    const availableEditOptions = useMemo(
        () =>
            EDIT_TYPE_OPTIONS.filter((opt) => {
                if (opt.value === 'line_items') return orderType?.toLowerCase() === 'ltl' && !isLineItemsLocked;

                if (PICKUP_SIDE_EDIT_TYPES.includes(opt.value)) return !isEveryLegPickupLocked;

                return true;
            }),
        [EDIT_TYPE_OPTIONS, orderType, isLineItemsLocked, isEveryLegPickupLocked]
    );

    // Pickup-side edits can only target legs that haven't departed yet
    const availableLegOptions = useMemo(
        () => (isPickupSideEditType ? legOptions.filter((opt) => !pickupLockedLegIds.has(opt.value)) : legOptions),
        [isPickupSideEditType, legOptions, pickupLockedLegIds]
    );

    // Leg dropdown: multi-leg orders only, and never for line items (dimensions are
    // order-level on the master, not per-leg)
    const isLegSelectVisible = hasMultipleLegs && !!selectedEditType && !isLineItemsEditType;

    // Subscribes to every schedule field (incl. times) so the change check below re-runs on any edit
    const scheduleValues = watch();

    const lineItemsBaseline = useMemo(() => mapExistingLineItems(existingLineItems ?? []), [existingLineItems]);

    // A request that carries exactly what the order already has is noise for operations —
    // compare the payload we would send against the same payload built from the prefill.
    // Deliberately NOT memoised: watch() hands back RHF's internal values object, whose
    // reference doesn't change on edits, so a dependency array would go stale.
    const hasChanges = (() => {
        if (!selectedEditType) return false;

        if (isPickupScheduleEditType) return !isSameJson(buildPickupEditData(scheduleValues), buildPickupEditData(schedulePrefill));

        if (isDeliveryScheduleEditType) return !isSameJson(buildDeliveryEditData(scheduleValues), buildDeliveryEditData(schedulePrefill));

        if (isAddressEditType) {
            const baseline = isShipper ? addressPrefills.shipper : addressPrefills.consignee;

            // Saved address picked → compare ids; brand-new address → compare its fields
            if (selectedAddressId) return selectedAddressId !== (baseline?.id ?? null);

            return !isSameJson(addressData, baseline?.data ?? null);
        }

        if (isLineItemsEditType) return !isSameJson(lineItems.map(normalizeLineItem), lineItemsBaseline.map(normalizeLineItem));

        return false;
    })();

    const isSubmitEnabled = useMemo(() => {
        if (!selectedEditType) return false;

        if (isLegSelectVisible && !selectedLeg) return false;

        if (!hasChanges) return false;

        if (isPickupScheduleEditType) {
            return !!pickupDate;
        }

        if (isDeliveryScheduleEditType) {
            return deliverySensitivity === 'open' || !!deliveryDate;
        }

        if (isAddressEditType) {
            return !isCreatingNew && !!addressData?.companyName && !!addressData?.fullAddress;
        }

        if (isLineItemsEditType) {
            return lineItems.some((item) => item.handlingUnitNo > 0 || item.weight > 0);
        }

        return false;
    }, [
        selectedEditType,
        isPickupScheduleEditType,
        isDeliveryScheduleEditType,
        isAddressEditType,
        isLineItemsEditType,
        isLegSelectVisible,
        selectedLeg,
        hasChanges,
        pickupDate,
        deliverySensitivity,
        deliveryDate,
        isCreatingNew,
        addressData,
        lineItems,
    ]);

    // A leg chosen for one edit type may be ineligible for the next (e.g. leg 1 picked for a
    // delivery edit, then the type switched to pickup while leg 1 is in transit) — snap to the
    // first eligible leg rather than submitting against a departed one.
    useEffect(() => {
        if (!isLegSelectVisible) return;

        if (selectedLeg && availableLegOptions.some((opt) => opt.value === selectedLeg.value)) return;

        setSelectedLeg(availableLegOptions[0] ?? null);
    }, [isLegSelectVisible, availableLegOptions, selectedLeg?.value]);

    // A stale conflict icon would mislead once the inputs change — clear it whenever
    // any date, the leg, or the edit type moves; the next submit re-validates.
    useEffect(() => {
        setCrossLegError(null);
    }, [pickupDate, pickupEndDate, deliveryDate, deliveryEndDate, selectedLeg?.value, selectedEditType?.value]);

    // From the Master tab the customer has no leg context, so default per edit type:
    // pickup/shipper edits target the FIRST leg, delivery/consignee the LAST — matching
    // how order-level requests have always been applied. A hand-picked leg is never overridden.
    useEffect(() => {
        if (legTouchedRef.current || !hasMultipleLegs || !orderLegActiveTab?.isMaster || !selectedEditType) return;

        const isDeliverySide = selectedEditType.value === 'delivery_schedule' || selectedEditType.value === 'consignee_details';
        setSelectedLeg((isDeliverySide ? availableLegOptions[availableLegOptions.length - 1] : availableLegOptions[0]) ?? null);
    }, [selectedEditType?.value, hasMultipleLegs, orderLegActiveTab?.isMaster, availableLegOptions]);

    // ─── Reset on edit type change ────────────────────────────────────────────

    useEffect(() => {
        const isScheduleEdit = selectedEditType?.value === 'pickup_schedule' || selectedEditType?.value === 'delivery_schedule';
        resetSchedule(isScheduleEdit ? schedulePrefill : SCHEDULE_DEFAULTS);

        const addrPrefill = selectedEditType?.value === 'shipper_details' ? addressPrefills.shipper : selectedEditType?.value === 'consignee_details' ? addressPrefills.consignee : null;
        setSelectedAddressId(addrPrefill?.id ?? null);
        setAddressData(addrPrefill?.data ?? null);
        setIsCreatingNew(false);
        setShowErrors(false);

        if (selectedEditType?.value === 'line_items') {
            setLineItems(lineItemsBaseline.length > 0 ? lineItemsBaseline.map((item) => ({ ...item })) : [{ ...NEW_LINE_ITEM }]);
        } else {
            setLineItems([]);
        }
        // lineItemsBaseline is intentionally NOT a dependency: it derives from Redux leg data, and a
        // live order update mid-edit must not wipe the customer's in-progress line-item changes.
    }, [selectedEditType?.value, schedulePrefill, addressPrefills]);

    const handlePickupSensitivityChange = (val: string, onChange: (v: string) => void) => {
        onChange(val);
        clearErrors('pickupDate');

        if (val !== 'flexible') {
            setValue('pickupEndDate', null);
            clearErrors('pickupEndDate');
        }
    };

    const handlePickupWindowTypeChange = (val: string, onChange: (v: string) => void) => {
        onChange(val);

        if (val === 'open') {
            setValue('pickupStartTime', '');
            setValue('pickupEndTime', '');
        } else if (val === 'appointment') {
            setValue('pickupEndTime', '');
        }
    };

    const handleDeliverySensitivityChange = (val: string, onChange: (v: string) => void) => {
        onChange(val);
        clearErrors('deliveryDate');

        if (val !== 'flexible') {
            setValue('deliveryEndDate', null);
            clearErrors('deliveryEndDate');
        }

        if (val === 'open') {
            setValue('deliveryDate', null);
        }
    };

    const handleDeliveryWindowTypeChange = (val: string, onChange: (v: string) => void) => {
        onChange(val);

        if (val === 'open') {
            setValue('deliveryStartTime', '');
            setValue('deliveryEndTime', '');
        } else if (val === 'appointment') {
            setValue('deliveryEndTime', '');
        }
    };

    const validateSchedule = async (): Promise<boolean> => {
        const schema = isPickupScheduleEditType ? pickupScheduleSchema : deliveryScheduleSchema;
        const values = getValues();

        try {
            await schema.validate(values, { abortEarly: false });

            return true;
        } catch (err: any) {
            if (err.inner) {
                err.inner.forEach((e: Yup.ValidationError) => {
                    setError(e.path as keyof IScheduleForm, { type: 'manual', message: e.message });
                });
            }

            return false;
        }
    };

    // Cross-leg / same-leg date-order validation, run AFTER the Yup schema passes.
    // Returns the offending field + a human message (fields only render a red border,
    // so the message is surfaced via toast). Equality is allowed — same-day handoff.
    const getScheduleCrossLegError = (): { field: keyof IScheduleForm; message: string } | null => {
        const sv = getValues();
        const toDay = (d: any): string | null => (d ? moment(d).format('YYYY-MM-DD') : null);
        const fmt = (d: string) => moment(d, 'YYYY-MM-DD').format('MMM Do, YYYY');

        if (isPickupScheduleEditType) {
            const pickup = toDay(sv.pickupDate);
            const pickupEnd = toDay(sv.pickupEndDate);
            const legDelivery = toDay(deliveryMaxDate);

            // Pickup can never pass this leg's own delivery date
            if (pickup && legDelivery && pickup > legDelivery) {
                return { field: 'pickupDate', message: t('Orders.Pickup_After_Leg_Delivery', { date: fmt(legDelivery) }) };
            }

            if (pickupEnd && legDelivery && pickupEnd > legDelivery) {
                return { field: 'pickupEndDate', message: t('Orders.Pickup_After_Leg_Delivery', { date: fmt(legDelivery) }) };
            }

            // Pickup can't start before the previous leg has delivered — that leg's
            // delivery schedule must be changed first
            const prev = adjacentLegBounds.prev;

            if (pickup && prev?.deliveryDate && pickup < prev.deliveryDate) {
                return { field: 'pickupDate', message: t('Orders.Pickup_Overlaps_Previous_Leg', { leg: `#${prev.legId}`, date: fmt(prev.deliveryDate) }) };
            }
        }

        if (isDeliveryScheduleEditType) {
            const delivery = toDay(sv.deliveryDate);
            const deliveryEnd = toDay(sv.deliveryEndDate);
            const legPickup = toDay(pickupMinDateForDelivery);

            // Delivery can never come before this leg's own pickup date
            if (delivery && legPickup && delivery < legPickup) {
                return { field: 'deliveryDate', message: t('Orders.Delivery_Before_Leg_Pickup', { date: fmt(legPickup) }) };
            }

            // Delivery can't run past the next leg's pickup — that leg's pickup
            // schedule must be changed first
            const next = adjacentLegBounds.next;

            if (next?.pickupDate) {
                if (delivery && delivery > next.pickupDate) {
                    return { field: 'deliveryDate', message: t('Orders.Delivery_Overlaps_Next_Leg', { leg: `#${next.legId}`, date: fmt(next.pickupDate) }) };
                }

                if (deliveryEnd && deliveryEnd > next.pickupDate) {
                    return { field: 'deliveryEndDate', message: t('Orders.Delivery_Overlaps_Next_Leg', { leg: `#${next.legId}`, date: fmt(next.pickupDate) }) };
                }
            }
        }

        return null;
    };

    // Red info icon + tooltip on the date field carrying a cross-leg conflict.
    // right-9 keeps it clear of DateSelect's calendar icon at the input's right edge.
    const renderCrossLegErrorIcon = (field: keyof IScheduleForm) =>
        crossLegError?.field === field ? (
            <span className="absolute right-9 bottom-2.5 z-[1]">
                <TooltipCmp message={crossLegError.message} position="top">
                    <InfoCircle className="w-4 h-4 text-red-500 cursor-pointer" />
                </TooltipCmp>
            </span>
        ) : null;

    const getAddressLabel = () => {
        if (selectedEditType?.value === 'shipper_details') return t('Orders.Change_Shipper_Details');
        if (selectedEditType?.value === 'consignee_details') return t('Orders.Change_Consignee_Details');

        return t('Common.Address');
    };

    const handleAddressSelect = (id: number, data: IAddressData) => {
        setSelectedAddressId(id);
        setAddressData(data);
        setIsCreatingNew(false);
    };

    const handleCreateNew = () => {
        setSelectedAddressId(null);
        setIsCreatingNew(true);
        setAddressData({
            id: null,
            companyName: '',
            fullAddress: '',
            address1: '',
            city: '',
            state: '',
            stateCode: '',
            country: '',
            countryCode: '',
            postal: '',
            latitude: '',
            longitude: '',
            streetNumber: '',
            timezone: '',
        });
    };

    const handleCancelNew = () => {
        setIsCreatingNew(false);
        setSelectedAddressId(null);
        setAddressData(null);
    };

    const handleNewAddressChange = (data: IAddressData) => {
        setAddressData(data);
    };

    const handleAddressSaved = (id: number, data: IAddressData) => {
        setSelectedAddressId(id);
        setAddressData(data);
        setIsCreatingNew(false);
    };

    const getAddressErrors = () => {
        if (!isAddressEditType || !addressData) return {};

        const errs: any = {};

        if (!addressData.companyName) errs.companyName = true;
        if (!addressData.fullAddress || !addressData.address1) errs.fullAddress = true;

        return errs;
    };

    const getLineItemsErrors = () => {
        if (!isLineItemsEditType) return [];

        return lineItems.map((item) => {
            const itemErrors: any = {};
            const wm = item.weightMeasurement?.toUpperCase() === 'KGS' ? MEASUREMENT.WEIGHT2.value : MEASUREMENT.WEIGHT1.value;
            const unit = wm === MEASUREMENT.WEIGHT2.value ? ' cm' : '"';
            const widthMax = getDimensionMaxByEquipment('WIDTH', equipmentType, wm);
            const heightMax = getDimensionMaxByEquipment('HEIGHT', equipmentType, wm);

            if (!item.handlingUnit) itemErrors.handlingUnit = true;
            if (!item.handlingUnitNo || item.handlingUnitNo <= 0) itemErrors.handlingUnitNo = true;
            if (!item.weight || item.weight <= 0) itemErrors.weight = true;
            if (!item.itemLength || item.itemLength <= 0) itemErrors.itemLength = true;

            if (!item.width || item.width <= 0) {
                itemErrors.width = true;
            } else if (widthMax !== null && item.width > widthMax) {
                itemErrors.width = t('ValidationMessages.Width_exceeds_max', { max: widthMax, unit, equipmentType: equipmentTypeFullName });
            }

            if (!item.height || item.height <= 0) {
                itemErrors.height = true;
            } else if (heightMax !== null && item.height > heightMax) {
                itemErrors.height =
                    equipmentType === 'F'
                        ? t('ValidationMessages.Height_step_deck_required', { max: heightMax, unit })
                        : t('ValidationMessages.Height_exceeds_max', { max: heightMax, unit, equipmentType: equipmentTypeFullName });
            }

            return itemErrors;
        });
    };

    const handleSubmit = async () => {
        setShowErrors(true);

        if (!selectedEditType) {
            WalToast.error(t('Orders.Select_an_edit_option'));

            return;
        }

        if (isAddressEditType && isCreatingNew) {
            WalToast.error(t('Orders.Please_save_or_cancel_new_address'));

            return;
        }

        if (isAddressEditType && !addressData) {
            WalToast.error(t('Orders.Address_is_required'));

            return;
        }

        if (isAddressEditType && addressData) {
            if (!addressData.companyName) {
                WalToast.error(t('Orders.Company_name_is_required'));

                return;
            }

            if (!addressData.fullAddress || !addressData.address1 || !addressData.city || !addressData.state || !addressData.country || !addressData.postal) {
                WalToast.error(t('Orders.Address_must_be_complete'));

                return;
            }
        }

        if (isLineItemsEditType) {
            const hasInvalidItems = lineItems.some((item) => {
                const wm = item.weightMeasurement?.toUpperCase() === 'KGS' ? MEASUREMENT.WEIGHT2.value : MEASUREMENT.WEIGHT1.value;
                const widthMax = getDimensionMaxByEquipment('WIDTH', equipmentType, wm);
                const heightMax = getDimensionMaxByEquipment('HEIGHT', equipmentType, wm);

                return (
                    !item.handlingUnit ||
                    item.handlingUnitNo <= 0 ||
                    item.weight <= 0 ||
                    item.itemLength <= 0 ||
                    item.width <= 0 ||
                    (widthMax !== null && item.width > widthMax) ||
                    item.height <= 0 ||
                    (heightMax !== null && item.height > heightMax)
                );
            });

            if (hasInvalidItems) return;
        }

        if (isPickupScheduleEditType || isDeliveryScheduleEditType) {
            const isValid = await validateSchedule();

            if (!isValid) return;

            // Date-order guard: same-leg pickup≤delivery plus adjacent-leg overlap.
            // Message renders as a red info icon + tooltip on the offending field.
            const dateConflict = getScheduleCrossLegError();

            if (dateConflict) {
                setError(dateConflict.field, { type: 'manual', message: dateConflict.message });
                setCrossLegError(dateConflict);

                return;
            }
        }

        // The button is already disabled for this case; kept as a hard stop so an unchanged
        // request can never reach operations through keyboard submit or a stale render.
        if (!hasChanges) {
            WalToast.error(t('Orders.No_changes_to_request'));

            return;
        }

        setIsLoading(true);

        try {
            let editData: any = {};
            const sv = getValues();

            if (isAddressEditType) {
                if (selectedAddressId) {
                    editData = { value: selectedAddressId };
                } else if (addressData) {
                    editData = {
                        value: null,
                        addressData: {
                            companyName: addressData.companyName,
                            fullAddress: addressData.fullAddress,
                            address1: addressData.address1,
                            city: addressData.city,
                            state: addressData.state,
                            stateCode: addressData.stateCode,
                            country: addressData.country,
                            countryCode: addressData.countryCode,
                            postal: addressData.postal,
                            latitude: addressData.latitude,
                            longitude: addressData.longitude,
                            streetNumber: addressData.streetNumber,
                            timezone: addressData.timezone,
                        },
                    };
                }
            } else if (isLineItemsEditType) {
                const validLineItems = lineItems.filter((item) => item.handlingUnit && item.handlingUnitNo > 0 && item.weight > 0 && item.itemLength > 0 && item.width > 0 && item.height > 0);
                editData = { value: validLineItems };
            } else if (isPickupScheduleEditType) {
                editData = buildPickupEditData(sv);
            } else if (isDeliveryScheduleEditType) {
                editData = buildDeliveryEditData(sv);
            }

            // Leg-scoped request only when the dropdown is shown (multi-leg, non-line-items);
            // single-leg orders omit the leg entirely so the backend keeps today's behavior.
            //
            // Consolidated view: a presentation leg can span several PHYSICAL legs
            // (mergedLegIds, first→last) while carrying the FIRST leg's id. Delivery/
            // consignee data shown for that span belongs to its LAST physical leg, so
            // resolve the id per edit type — otherwise the approval would edit an
            // internal cross-dock leg.
            let orderLegId: number | undefined;

            if (isLegSelectVisible && selectedLeg) {
                const mergedLegIds: number[] = Array.isArray(selectedLegData?.mergedLegIds) ? selectedLegData.mergedLegIds : [];
                const isDeliverySide = selectedEditType.value === 'delivery_schedule' || selectedEditType.value === 'consignee_details';
                orderLegId = mergedLegIds.length > 1 && isDeliverySide ? mergedLegIds[mergedLegIds.length - 1] : Number(selectedLeg.value);
            }

            const response = await createEditRequest(orderId, selectedEditType.value, editData, description.trim(), orderLegId);

            WalToast.success(t('Orders.Edit_request_submitted'));
            // Axios interceptor returns response.data for 200, full response for 201 — handle both
            onSuccess?.(response?.data?.data?.request ?? response?.data?.request, response?.data?.data?.activity ?? response?.data?.activity);
            handleClose();
        } catch (err: any) {
            await logError(err, {
                errorMessage: err?.message || 'Error submitting edit request',
                errorLocation: 'EditOrderModal: handleSubmit',
                storeInDatabase: true,
                logToConsole: true,
            });
            WalToast.error(err?.response?.data?.message || t('Orders.Edit_request_failed'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CommonModal
            title={t('Orders.Edit_Order')}
            handleClose={handleClose}
            size="max-w-[688px]"
            isOverflow={true}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={handleClose}
            secondaryBtnDisabled={isLoading}
            primaryBtnText={t('Orders.Send_Request')}
            primaryBtnOnClick={handleSubmit}
            primaryBtnLoading={isLoading}
            primaryBtnDisabled={isLoading || !isSubmitEnabled}
        >
            <div className="p-4 space-y-4">
                {/* Edit Type Selector */}
                <SelectBox
                    value={selectedEditType}
                    options={availableEditOptions}
                    label={t('Orders.Select_Edit_Option')}
                    labelClassName="form_label block mb-1.5"
                    placeholder="Select edit request"
                    onChangeFunc={(selected: any) => setSelectedEditType(selected)}
                    isDisabled={isLoading}
                    required
                />

                {/* Leg Selector — multi-leg orders only; prefills above follow this selection */}
                {isLegSelectVisible && (
                    <SelectBox
                        value={selectedLeg}
                        options={availableLegOptions}
                        label={t('Orders.Select_Leg')}
                        labelClassName="form_label block mb-1.5"
                        placeholder={t('Orders.Select_Leg')}
                        onChangeFunc={(selected: any) => {
                            legTouchedRef.current = true;
                            setSelectedLeg(selected);
                        }}
                        isDisabled={isLoading}
                        required
                    />
                )}

                {isPickupScheduleEditType && (
                    <>
                        <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                            <Controller
                                name="pickupSensitivity"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        label={t('Orders.Pickup_Sensitivity')}
                                        labelClassName="form_label block mb-1.5"
                                        parentClassName="w-full"
                                        options={PICKUP_DATE_TYPE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                                        value={PICKUP_DATE_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                                        onChangeFunc={(selected: any) => handlePickupSensitivityChange(selected?.value, onChange)}
                                        placeholder={t('Orders.Select_Pickup_Sensitivity')}
                                        isDisabled={isLoading}
                                        isError={!!errors.pickupSensitivity}
                                        required
                                    />
                                )}
                            />

                            <div className="flex gap-x-3 items-end">
                                <Controller
                                    name="pickupDate"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <div className="relative w-full">
                                            {renderCrossLegErrorIcon('pickupDate')}
                                            <DateSelect
                                                inputName="pickupDate"
                                                labelClassName="form_label block mb-1.5"
                                                placeholder={t('Common.Start_date')}
                                                dateFormat="dddd, MMMM Do, YYYY"
                                                selected={value}
                                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                                onChangeFunc={(e: any) => {
                                                    onChange(e.value);
                                                    clearErrors('pickupDate');

                                                    // Clear end date if it falls before the new start date
                                                    if (pickupEndDate && e.value && moment(pickupEndDate).isBefore(moment(e.value), 'day')) {
                                                        setValue('pickupEndDate', null);
                                                    }
                                                }}
                                                minDate={pickupMinDate}
                                                maxDate={deliveryMaxDate}
                                                disabled={isLoading}
                                                isDisplayIcon={true}
                                                parentClassName="w-full"
                                                errorText={!!errors.pickupDate}
                                                required
                                            />
                                        </div>
                                    )}
                                />

                                {pickupSensitivity === 'flexible' && (
                                    <Controller
                                        name="pickupEndDate"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <div className="relative w-full">
                                                {renderCrossLegErrorIcon('pickupEndDate')}
                                                <DateSelect
                                                    inputName="pickupEndDate"
                                                    labelClassName="form_label block mb-1.5"
                                                    placeholder={t('Common.End_date')}
                                                    dateFormat="dddd, MMMM Do, YYYY"
                                                    selected={value}
                                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                                    onChangeFunc={(e: any) => {
                                                        onChange(e.value);
                                                        clearErrors('pickupEndDate');
                                                    }}
                                                    minDate={pickupDate ?? pickupMinDate}
                                                    maxDate={deliveryMaxDate}
                                                    disabled={isLoading}
                                                    isDisplayIcon={true}
                                                    parentClassName="w-full"
                                                    errorText={!!errors.pickupEndDate}
                                                    required
                                                />
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                            <div className="flex flex-col w-full">
                                <div className="flex gap-x-3 items-end">
                                    <Controller
                                        name="pickupWindowType"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <div className="flex-1">
                                                <SelectBox
                                                    label={t('BookOrder.Pickup_Window')}
                                                    labelClassName="form_label block mb-1.5"
                                                    parentClassName="w-full"
                                                    options={PICKUP_WINDOW_TYPE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                                                    value={PICKUP_WINDOW_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                                                    onChangeFunc={(selected: any) => handlePickupWindowTypeChange(selected?.value, onChange)}
                                                    placeholder={t('Orders.Select_Pickup_Window')}
                                                    isDisabled={isLoading}
                                                    isError={!!errors.pickupWindowType}
                                                    required
                                                />
                                            </div>
                                        )}
                                    />

                                    {pickupWindowType && pickupWindowType !== 'open' && (
                                        <Controller
                                            name="pickupStartTime"
                                            control={control}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <div className="flex-1">
                                                    <InputMaskCmp
                                                        inputName="pickupStartTime"
                                                        placeholder="HH:MM"
                                                        parentClassName="flex-1"
                                                        value={(value as string) ?? ''}
                                                        HandleChange={(e: any) => {
                                                            onChange(formatTimeInput(e.target.value, (value as string) ?? ''));
                                                            clearErrors('pickupStartTime');
                                                            clearErrors('pickupEndTime');
                                                        }}
                                                        mask="99:99"
                                                        isError={!!error}
                                                        icon={
                                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                                <Clock className="h-4 w-4" />
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                            )}
                                        />
                                    )}

                                    {pickupWindowType === 'ready_time' && (
                                        <Controller
                                            name="pickupEndTime"
                                            control={control}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <div className="flex-1">
                                                    <InputMaskCmp
                                                        inputName="pickupEndTime"
                                                        placeholder="HH:MM"
                                                        parentClassName="flex-1"
                                                        value={(value as string) ?? ''}
                                                        HandleChange={(e: any) => {
                                                            onChange(formatTimeInput(e.target.value, (value as string) ?? ''));
                                                            clearErrors('pickupEndTime');
                                                        }}
                                                        mask="99:99"
                                                        isError={!!error}
                                                        icon={
                                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                                <Clock className="h-4 w-4" />
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>

                                {pickupWindowType && PICKUP_WINDOW_TOOLTIP?.[pickupWindowType] && (
                                    <span className="mt-1.5 text-[12px] font-normal leading-[18px] text-grayLight600 italic">
                                        {pickupWindowType === WINDOW_TYPE.OPEN && shipperBusinessHours
                                            ? PICKUP_WINDOW_TOOLTIP[pickupWindowType].replace('business hours', `business hours (${shipperBusinessHours})`)
                                            : PICKUP_WINDOW_TOOLTIP[pickupWindowType]}
                                    </span>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {isDeliveryScheduleEditType && (
                    <>
                        <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                            <Controller
                                name="deliverySensitivity"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        label={t('Orders.Delivery_Sensitivity')}
                                        labelClassName="form_label block mb-1.5"
                                        parentClassName="w-full"
                                        options={DELIVERY_DATE_TYPE_OPTIONS.filter((opt) => opt.value !== 'open').map((opt) => ({ label: opt.label, value: opt.value }))}
                                        value={DELIVERY_DATE_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                                        onChangeFunc={(selected: any) => handleDeliverySensitivityChange(selected?.value, onChange)}
                                        placeholder={t('Orders.Select_Delivery_Sensitivity')}
                                        isDisabled={isLoading}
                                        isError={!!errors.deliverySensitivity}
                                        required
                                    />
                                )}
                            />

                            {deliverySensitivity !== 'open' && (
                                <div className="flex gap-x-3 items-end">
                                    <Controller
                                        name="deliveryDate"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <div className="relative w-full">
                                                {renderCrossLegErrorIcon('deliveryDate')}
                                                <DateSelect
                                                    inputName="deliveryDate"
                                                    labelClassName="form_label block mb-1.5"
                                                    placeholder={t('Common.Start_date')}
                                                    dateFormat="dddd, MMMM Do, YYYY"
                                                    selected={value}
                                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                                    onChangeFunc={(e: any) => {
                                                        onChange(e.value);
                                                        clearErrors('deliveryDate');

                                                        if (deliveryEndDate && e.value && moment(deliveryEndDate).isBefore(moment(e.value), 'day')) {
                                                            setValue('deliveryEndDate', null);
                                                        }
                                                    }}
                                                    minDate={pickupMinDateForDelivery}
                                                    disabled={isLoading}
                                                    isDisplayIcon={true}
                                                    parentClassName="w-full"
                                                    errorText={!!errors.deliveryDate}
                                                    required
                                                />
                                            </div>
                                        )}
                                    />

                                    {deliverySensitivity === 'flexible' && (
                                        <Controller
                                            name="deliveryEndDate"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <div className="relative w-full">
                                                    {renderCrossLegErrorIcon('deliveryEndDate')}
                                                    <DateSelect
                                                        inputName="deliveryEndDate"
                                                        labelClassName="form_label block mb-1.5"
                                                        placeholder={t('Common.End_date')}
                                                        dateFormat="dddd, MMMM Do, YYYY"
                                                        selected={value}
                                                        value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                                        onChangeFunc={(e: any) => {
                                                            onChange(e.value);
                                                            clearErrors('deliveryEndDate');
                                                        }}
                                                        minDate={deliveryDate ?? pickupMinDateForDelivery}
                                                        disabled={isLoading}
                                                        isDisplayIcon={true}
                                                        parentClassName="w-full"
                                                        errorText={!!errors.deliveryEndDate}
                                                        required
                                                    />
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                            <div className="flex flex-col w-full">
                                <div className="flex gap-x-3 items-end">
                                    <Controller
                                        name="deliveryWindowType"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <div className="flex-1">
                                                <SelectBox
                                                    label={t('BookOrder.Delivery_Window')}
                                                    labelClassName="form_label block mb-1.5"
                                                    parentClassName="w-full"
                                                    options={DELIVERY_WINDOW_TYPE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                                                    value={DELIVERY_WINDOW_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                                                    onChangeFunc={(selected: any) => handleDeliveryWindowTypeChange(selected?.value, onChange)}
                                                    placeholder={t('Orders.Select_Delivery_Window')}
                                                    isDisabled={isLoading}
                                                    isError={!!errors.deliveryWindowType}
                                                    required
                                                />
                                            </div>
                                        )}
                                    />

                                    {deliveryWindowType && deliveryWindowType !== 'open' && (
                                        <Controller
                                            name="deliveryStartTime"
                                            control={control}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <div className="flex-1">
                                                    <InputMaskCmp
                                                        inputName="deliveryStartTime"
                                                        placeholder="HH:MM"
                                                        parentClassName="flex-1"
                                                        value={(value as string) ?? ''}
                                                        HandleChange={(e: any) => {
                                                            onChange(formatTimeInput(e.target.value, (value as string) ?? ''));
                                                            clearErrors('deliveryStartTime');
                                                            clearErrors('deliveryEndTime');
                                                        }}
                                                        mask="99:99"
                                                        isError={!!error}
                                                        icon={
                                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                                <Clock className="h-4 w-4" />
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                            )}
                                        />
                                    )}

                                    {deliveryWindowType === 'delivery_time' && (
                                        <Controller
                                            name="deliveryEndTime"
                                            control={control}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <div className="flex-1">
                                                    <InputMaskCmp
                                                        inputName="deliveryEndTime"
                                                        placeholder="HH:MM"
                                                        parentClassName="flex-1"
                                                        value={(value as string) ?? ''}
                                                        HandleChange={(e: any) => {
                                                            onChange(formatTimeInput(e.target.value, (value as string) ?? ''));
                                                            clearErrors('deliveryEndTime');
                                                        }}
                                                        mask="99:99"
                                                        isError={!!error}
                                                        icon={
                                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                                <Clock className="h-4 w-4" />
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>

                                {deliveryWindowType && DELIVERY_WINDOW_TOOLTIP?.[deliveryWindowType] && (
                                    <span className="mt-1.5 text-[12px] font-normal leading-[18px] text-grayLight600 italic">
                                        {deliveryWindowType === WINDOW_TYPE.OPEN && consigneeBusinessHours
                                            ? DELIVERY_WINDOW_TOOLTIP[deliveryWindowType].replace('business hours', `business hours (${consigneeBusinessHours})`)
                                            : DELIVERY_WINDOW_TOOLTIP[deliveryWindowType]}
                                    </span>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* ── Address Selection (shipper / consignee) — unchanged ── */}
                {isAddressEditType && (
                    <AddressSelectionSection
                        isShipper={isShipper}
                        label={getAddressLabel()}
                        selectedAddressId={selectedAddressId}
                        addressData={addressData}
                        isCreatingNew={isCreatingNew}
                        onAddressSelect={handleAddressSelect}
                        onCreateNew={handleCreateNew}
                        onCancelNew={handleCancelNew}
                        onNewAddressChange={handleNewAddressChange}
                        onAddressSaved={handleAddressSaved}
                        errors={showErrors ? getAddressErrors() : {}}
                        isLoading={isLoading || isShipperLoading}
                    />
                )}

                {/* ── Line Items — unchanged ── */}
                {isLineItemsEditType && <LineItemsSection lineItems={lineItems} onLineItemsChange={setLineItems} errors={showErrors ? getLineItemsErrors() : []} isLoading={isLoading} />}

                {/* ── Description — unchanged ── */}
                <InputTextArea
                    inputName="description"
                    label={t('Common.Description')}
                    placeholder={t('Orders.Add_reason_for_the_change_request')}
                    value={description}
                    onChangeFunc={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    labelClassName="block mb-1.5"
                    className="h-[104px] !outline-none focus:!outline-none focus:!ring-0 !shadow-none focus:!shadow-none"
                    maxLength={500}
                    labelIconRight={<span className="text-xs text-grayLight600 font-normal mb-1.5">{description.length}/500</span>}
                    disabled={isLoading}
                />
            </div>
        </CommonModal>
    );
};

export default EditOrderModal;
